import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'

import {
  Box, DashboardModule, Link, StatusBadge, Text,
} from '@campaignhub/suit-theme'

import { useSetState } from '@campaignhub/react-hooks'
import PageContext from '@contexts/pageContext'
import useCurrentUser from '@hooks/useCurrentUser'

import { getServiceStatusColor } from '@functions/getServiceStatusColor'
import TasksImageUrl from '@components/assets/tasks.svg'
import userTypes from '@enums/userTypes'

const defaultState = {
  agent: '',
  dusktotalImages: 0,
  otherAttendees: [],
  relatedDescription: [],
  rsDetails: '',
  serviceDescription: [],
  siteplan: '',
  workbaseDetails: '',
  contentExpressDetails: '',
}

const JobSummary = (props) => {
  const { details, hidden, serviceJobLoading, showJobLength } = props

  const [state, setState] = useSetState(defaultState)
  const { agent, otherAttendees, relatedDescription, rsDetails, serviceDescription, siteplan, workbaseDetails, contentExpressDetails } = state

  const { serviceJob, showDuskPositional, serviceJobs } = useContext(PageContext)
  const { campaign, client, ctReferenceId, poNumber, totalAssets } = serviceJob

  const { currentUser: { timeZone }, currentUser } = useCurrentUser()

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceJobStatuses } = entities

  const duskServiceJob = Object.values(serviceJobs).filter(x => ['Dusk Photography'].includes(x.service?.name) && x.campaign?.id === serviceJob.campaign.id)[0]

  useEffect(() => {
    if (details) {
      setState({
        agent: details.find(x => x.entityFieldType.name === 'Agent')?.value || '',
        serviceDescription: details.filter(x => x.entityFieldType.name === 'Description' && !(x.value && (x.value.includes('Product Name') || x.value.includes('Uploaded Urls:')))),
        otherAttendees: details.filter(x => x.entityFieldType.name === 'OtherAttendee'),
        relatedDescription: details.filter(x => x.entityFieldType.name === 'RelatedDetails' && !(x.value && (x.value.includes('Product Name') || x.value.includes('Uploaded Urls:')))),
        rsDetails: details.find(x => x.entityFieldType.name === 'RealStudiosDetails')?.value || '',
        siteplan: details.find(x => x.entityFieldType.name === 'HasSiteplan')?.value || '',
        workbaseDetails: details.find(x => x.entityFieldType.name === 'WorkbaseDetails')?.value || '',
        contentExpressDetails: details.find(x => x.entityFieldType.name === 'ContentExpressDetails')?.value || '',
      })
    }
  }, [details])

  const now = DateTime.fromISO(DateTime.now(), { zone: timeZone })
  const today = now.startOf('day')
  const daysOffset = now > today.set({ hour: 18, minute: 0 }) ? 1 : 0
  const offsetDate = today.plus({ days: daysOffset }).endOf('day')

  if (hidden) return null

  return (
    <DashboardModule
      title="Job Summary"
      headerRight={

        <Box display="block" width="auto" textAlign="end">
          {serviceJob?.campaign?.critical && <StatusBadge color="red" text="Critical" />}
          {!serviceJobLoading && currentUser?.userRole.userTypeId !== userTypes.find(x => x.name === 'OnSite').id ? (
            <>
              <StatusBadge boxProps={{ marginLeft: 'small' }} color="#282B42" ghost text={serviceJob.service?.name} />
              <StatusBadge
                boxProps={{ marginLeft: 'small' }}
                color={getServiceStatusColor(serviceJobStatuses[serviceJob.serviceJobStatusId]?.name)}
                ghost
                text={serviceJobStatuses[serviceJob.serviceJobStatusId]?.description}
              />
              <StatusBadge
                boxProps={{ marginLeft: 'small' }}
                ghost
                color={workbaseDetails ? '#22b8cf' : contentExpressDetails ? '#152342' : rsDetails ? 'green' : 'orange'}
                text={workbaseDetails ? 'CH' : rsDetails ? 'RH' : contentExpressDetails ? 'Content Express' : 'CT'}
              />
            </>
          ) : null}
        </Box>
      }
    >
      <Box
        backgroundColor="white"
        borderRadius={5}
        flexDirection={['column-reverse', 'row']}
        padding="large"
      >
        <Box
          flexDirection="column"
          marginRight={[0, 'large']}
          width={['100%', '70%', '75%']}
        >
          <Box flexDirection={['column', 'row', 'row']} gridGap="large">
            <Box flexDirection="column">
              <Box flexDirection="column" marginBottom="large">
                <Text marginBottom="medium" fontSize="small">Appointment Details</Text>
                <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                  {campaign?.name}
                </Text>
                {serviceJob.service?.onSite && (
                  <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                    {DateTime.fromISO(serviceJob.dateService, { zone: timeZone }).toFormat('LLLL dd, y, hh:mm a')}
                    {showJobLength ? ` (${!serviceJob.serviceJobLength ? 0 : serviceJob.serviceJobLength} minutes)` : ''}
                  </Text>
                )}
              </Box>
              {currentUser?.userRole.userTypeId !== userTypes.find(x => x.name === 'OnSite').id ||
                (currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'OnSite').id
                  && DateTime.fromISO(serviceJob.dateService, { zone: timeZone }) < offsetDate) ? (
                <>
                  <Box flexDirection="column">
                    <Text marginBottom="medium" fontSize="small">Agency Details</Text>
                    <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                      {client?.name}
                    </Text>
                    {agent && (
                      <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                        {JSON.parse(agent).name} {JSON.parse(agent).contactNumber}
                      </Text>
                    )}
                  </Box>
                  {rsDetails && (
                    <Box flexDirection="column" marginTop="large">
                      <Text marginBottom="medium" fontSize="small">RH Contact</Text>
                      <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                        {JSON.parse(rsDetails).value.slice(JSON.parse(rsDetails).value.indexOf('Agent'), JSON.parse(rsDetails).value.indexOf('Order') - 2)}
                      </Text>
                    </Box>
                  )}
                </>
              ) : null}
            </Box>
            <Box flexDirection="column">
              <Box flexDirection="column" marginBottom="large">
                <Text marginBottom="medium" fontSize="small">Product Name</Text>
                <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                  {serviceJob.name}
                </Text>
              </Box>
              <Text marginBottom="medium" fontSize="small">Order Details</Text>
              <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                {serviceJob.service?.name}
              </Text>
              {serviceDescription.map(desc => (
                <Text key={desc.id} color="bodyFontLightColor" marginBottom="small" fontSize="small">
                  {JSON.parse(desc.value).value}
                </Text>
              ))}
              {siteplan && JSON.parse(siteplan)?.value && (
                <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                  Has Siteplan
                </Text>
              )}
              <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                Total Assets: {totalAssets || '0'}
              </Text>
              {showDuskPositional && (
                <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                  Dusk Positionals: {duskServiceJob?.totalAssets}
                </Text>
              )}
              {(ctReferenceId) && (
                <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                  {`Jobtrack Job No: ${ctReferenceId}`}
                </Text>
              )}
              {(poNumber) && (
                <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                  {`PO Number: ${poNumber}`}
                </Text>
              )}
              {otherAttendees.length > 0 && (
                <Box flexDirection="row" marginBottom="small">
                  <Text style={{ flexShrink: 0 }} color="bodyFontLightColor" fontSize="small" marginRight="small">
                    Other Attendees:
                  </Text>
                  <Box flexDirection="column">
                    {otherAttendees.map(attendee => (
                      <Text key={attendee.value} color="bodyFontLightColor" marginBottom="small" fontSize="small">
                        {JSON.parse(attendee.value).name} {JSON.parse(attendee.value).contactNumber}
                      </Text>
                    ))}
                  </Box>
                </Box>
              )}
              {showDuskPositional && (
                <Text color="bodyFontLightColor" marginBottom="small" fontSize="small">
                  Dusk: {duskServiceJob?.serviceUsername}
                </Text>
              )}
              {relatedDescription.length > 0 && (
                <Box flexDirection="column" marginTop="large">
                  <Text marginBottom="medium" fontSize="small">Related Jobs Details</Text>
                  {relatedDescription.map((desc) => {
                    const descriptionValue = JSON.parse(desc.value).value
                    const jobIdMatch = descriptionValue?.match(/^(.*Job ID:\s)(\d+)$/)

                    return (
                      <Text key={desc.id} color="bodyFontLightColor" marginBottom="small" fontSize="small">
                        {jobIdMatch ? (
                          <>
                            {jobIdMatch[1]}
                            <Link href={`/service-jobs/${jobIdMatch[2]}`} target="_blank">{jobIdMatch[2]}</Link>
                          </>
                        ) : (
                          descriptionValue
                        )}
                      </Text>
                    )
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          alignItems="center"
          justifyContent="center"
          marginLeft={[0, 'auto']}
          marginBottom={['large', 0]}
          width={['100%', '30%', '25%']}
        >
          <img alt="Blank State" src={TasksImageUrl} style={{ maxWidth: '100%' }} />
        </Box>

      </Box>

    </DashboardModule>
  )
}

JobSummary.propTypes = {
  details: PropTypes.array,
  hidden: PropTypes.bool,
  serviceJobLoading: PropTypes.bool,
  showJobLength: PropTypes.bool,
}

export default JobSummary
